/**
 * FrostedCoverModal — Apple Home 風格毛玻璃 Blinds / Curtain 控制面板
 *
 * 設計語言：
 * - Warm Amber tint → 窗簾/百葉窗 專用色調
 * - Vertical slider track → fill from top = 簾子放下 = closed
 * - Open / Stop / Close 快速操作
 * - Preset buttons (25% / 50% / 75% / 100%)
 * - Shared by BLINDS and CURTAIN (only icon differs)
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 *
 * 方向邏輯：
 * - fill 從上往下 = 簾子放下 = 越 closed
 * - openPercent 0 = Closed (fill 100%), openPercent 100 = Open (fill 0%)
 * - API 指令：openClose with { openPercent }
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './FrostedCoverModal.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const BLINDS_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'slider', label: 'Position Slider', icon: 'blinds', slots: [
    { id: 'slider-track', label: 'Track', editableProps: ['background', 'borderRadius', 'border'] },
    { id: 'slider-fill', label: 'Fill', editableProps: ['background'] },
    { id: 'slider-percent', label: 'Percentage Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
  ]},
  { id: 'actions', label: 'Quick Actions', icon: 'open_in_full', slots: [
    { id: 'action-btn', label: 'Action Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'presets', label: 'Presets', icon: 'tune', slots: [
    { id: 'preset-btn', label: 'Preset Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
]

export const CURTAIN_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'slider', label: 'Position Slider', icon: 'curtains', slots: [
    { id: 'slider-track', label: 'Track', editableProps: ['background', 'borderRadius', 'border'] },
    { id: 'slider-fill', label: 'Fill', editableProps: ['background'] },
    { id: 'slider-percent', label: 'Percentage Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
  ]},
  { id: 'actions', label: 'Quick Actions', icon: 'open_in_full', slots: [
    { id: 'action-btn', label: 'Action Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'presets', label: 'Presets', icon: 'tune', slots: [
    { id: 'preset-btn', label: 'Preset Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
]

const DEFAULT_BLINDS_ORDER = BLINDS_FROSTED_SECTIONS.map((s) => s.id)
const DEFAULT_CURTAIN_ORDER = CURTAIN_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRESETS = [
  { id: '25', label: '25%', value: 25 },
  { id: '50', label: '50%', value: 50 },
  { id: '75', label: '75%', value: 75 },
] as const

const ACTIONS = [
  { id: 'open', label: 'Open', icon: 'arrow_upward' },
  { id: 'close', label: 'Close', icon: 'arrow_downward' },
] as const

type CoverVariant = 'blinds' | 'curtain'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// ---------------------------------------------------------------------------
// Internal shared component
// ---------------------------------------------------------------------------

interface CoverModalProps extends DeviceModalTemplateProps {
  variant: CoverVariant
}

function FrostedCoverModalInner({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
  credentials,
  variant,
}: CoverModalProps) {

  const defaultOrder = variant === 'blinds' ? DEFAULT_BLINDS_ORDER : DEFAULT_CURTAIN_ORDER

  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : defaultOrder
    const remaining = defaultOrder.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder, defaultOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  // openPercent: 0 = closed, 100 = fully open
  const currentOpenPercent = numAttr(deviceState, 'openPercent', 'openState', 'currentPosition') ?? 0

  // ---- Settings page ----
  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedField(label)
    setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ---- Rename ----
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])

  const startRename = useCallback(() => {
    setRenameValue(displayName)
    setIsRenaming(true)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [displayName])

  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) {
      setIsRenaming(false)
      return
    }
    setDisplayName(trimmed)
    setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ---- Local UI state (optimistic) ----
  const [pendingPercent, setPendingPercent] = useState<number | null>(null)
  const [activeAction, setActiveAction] = useState<string | null>(null)
  const displayPercent = pendingPercent ?? currentOpenPercent

  // Sync pending
  useEffect(() => {
    if (pendingPercent !== null && pendingPercent === currentOpenPercent) setPendingPercent(null)
  }, [currentOpenPercent, pendingPercent])

  // ---- Internal API caller ----
  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  // ---- Commands ----
  // Use standard "OpenClose" command with openPercent for percentage control
  // Simplified "open"/"close" commands force 100%/0% and ignore params
  const setPosition = useCallback((openPct: number) => {
    if (isEditorMode) return
    const clamped = Math.max(0, Math.min(100, openPct))
    setPendingPercent(clamped)
    execCmd('OpenClose', { openPercent: clamped })
  }, [isEditorMode, execCmd])

  const handleAction = useCallback((actionId: string) => {
    if (isEditorMode) return
    setActiveAction(actionId)
    switch (actionId) {
      case 'open':
        setPendingPercent(100)
        execCmd('OpenClose', { openPercent: 100 })
        break
      case 'close':
        setPendingPercent(0)
        execCmd('OpenClose', { openPercent: 0 })
        break
    }
  }, [isEditorMode, execCmd])

  // ---- Track drag → set position (pointer events for touch + mouse) ----
  const trackRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const dragCommitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const yToOpenPct = useCallback((clientY: number) => {
    const track = trackRef.current
    if (!track) return 0
    const rect = track.getBoundingClientRect()
    const ratio = (clientY - rect.top) / rect.height
    return Math.round(Math.max(0, Math.min(100, (1 - ratio) * 100)))
  }, [])

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isEditorMode) return
    isDraggingRef.current = true
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    const pct = yToOpenPct(e.clientY)
    setPendingPercent(pct)
  }, [isEditorMode, yToOpenPct])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return
    const pct = yToOpenPct(e.clientY)
    setPendingPercent(pct) // instant visual feedback, no API call yet
  }, [yToOpenPct])

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    const pct = yToOpenPct(e.clientY)
    // Debounce: send final position to API
    if (dragCommitTimer.current) clearTimeout(dragCommitTimer.current)
    dragCommitTimer.current = setTimeout(() => {
      setPosition(pct)
    }, 100)
  }, [yToOpenPct, setPosition])

  // ---- Derived values ----
  const fillHeight = 100 - displayPercent // fill from top = coverage
  const isClosed = displayPercent === 0
  const stateClass = isClosed ? 'cover-frosted--closed' : 'cover-frosted--open'
  const isFullyOpen = displayPercent >= 100
  const statusText = isClosed ? 'Closed' : isFullyOpen ? 'Open' : `Open ${displayPercent}%`
  const coverIcon = variant === 'blinds'
    ? (isClosed ? 'blinds_closed' : 'blinds')
    : (isClosed ? 'curtains_closed' : 'curtains')

  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  // Active preset
  const activePreset = PRESETS.find(p => p.value === displayPercent)?.id || null

  // ---- Render sections ----
  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null

    switch (sectionId) {
      case 'slider':
        return (
          <div key="slider" className={`cover-frosted__slider-area${hl('slider')}`} data-modal-section="slider">
            <div
              className="cover-frosted__track"
              ref={trackRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              <div className="cover-frosted__fill" style={{ height: `${fillHeight}%` }} />
              <div className="cover-frosted__percent">
                {isClosed ? 'Closed' : isFullyOpen ? 'Open' : <>{displayPercent}<span className="cover-frosted__percent-unit">%</span></>}
              </div>
              <div className="cover-frosted__icon-area">
                <span className="cover-frosted__cover-icon material-symbols-outlined">{coverIcon}</span>
              </div>
            </div>
          </div>
        )

      case 'actions':
        return (
          <div key="actions" className={`cover-frosted__actions${hl('actions')}`} data-modal-section="actions">
            {ACTIONS.map(a => (
              <button
                key={a.id}
                className={`cover-frosted__action-btn${activeAction === a.id ? ' active' : ''}`}
                onClick={() => handleAction(a.id)}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>{a.icon}</span>
                {a.label}
              </button>
            ))}
          </div>
        )

      case 'presets':
        return (
          <div key="presets" className={`cover-frosted__presets${hl('presets')}`} data-modal-section="presets">
            {PRESETS.map(p => (
              <button
                key={p.id}
                className={`cover-frosted__preset-btn${activePreset === p.id ? ' active' : ''}`}
                onClick={() => setPosition(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div
      className={`cover-frosted ${stateClass}`}
      data-modal-template-id={`${variant}-frosted`}
      onContextMenu={handleContextMenu}
    >
      {cssOverrides && <style>{cssOverrides}</style>}

      {/* Header */}
      <div className="cover-frosted__header" data-modal-section="_modal">
        <button className="cover-frosted__back" onClick={onClose} aria-label="Back">&lsaquo;</button>
        <span className="cover-frosted__name">{displayName}</span>
      </div>

      {/* Status */}
      <div className="cover-frosted__status">{statusText}</div>

      {/* Ordered sections */}
      {orderedSections.map(id => renderSection(id))}

      {/* Settings button */}
      <button
        className="cover-frosted__settings-btn"
        onClick={() => setShowSettings(true)}
        aria-label="Settings"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 28 }}>settings</span>
      </button>

      {/* Settings overlay */}
      {showSettings && (
        <div className="cover-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="cover-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="cover-frosted__settings-header">
              <span className="cover-frosted__settings-title">Settings</span>
              <button className="cover-frosted__settings-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>

            {/* Rename */}
            <div className="cover-frosted__settings-section">
              <div className="cover-frosted__settings-section-title">Device Name</div>
              {isRenaming ? (
                <div className="cover-frosted__settings-row">
                  <input
                    ref={renameInputRef}
                    className="cover-frosted__rename-input"
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename}
                    maxLength={40}
                  />
                </div>
              ) : (
                <div className="cover-frosted__settings-row">
                  <span className="cover-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>
                    {displayName}
                  </span>
                  <button className="cover-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>

            {/* Device Info */}
            <div className="cover-frosted__settings-section">
              <div className="cover-frosted__settings-section-title">Device Info</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="cover-frosted__settings-row">
                  <span className="cover-frosted__settings-label">{item.label}</span>
                  <span className="cover-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button
                      className="cover-frosted__settings-copy"
                      onClick={() => copyToClipboard(item.value, item.label)}
                      title="Copy"
                    >
                      📋
                      {copiedField === item.label && (
                        <span className="cover-frosted__copied-toast">Copied</span>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Public exports — thin wrappers
// ---------------------------------------------------------------------------

export function FrostedBlindsModal(props: DeviceModalTemplateProps) {
  return <FrostedCoverModalInner {...props} variant="blinds" />
}

export function FrostedCurtainModal(props: DeviceModalTemplateProps) {
  return <FrostedCoverModalInner {...props} variant="curtain" />
}
